import React, { useMemo, useState, useEffect, useRef, useContext } from 'react';
import Layout from '../components/Layout';
import DateRangePicker from '../components/Attendance/DateRangePicker';
import Pagination from '../components/Common/Pagination';
import { Attendance } from '../types/Attendance';
import { fetchAttendances } from '../api/Attendance';
import { fetchUsers } from '../api/User';
import { User } from '../types/User';
import { AuthContext } from '../context/AuthContext';
import { usePermissions, useRole } from '../hooks/usePermissions';
import { filterEnabledRegularUsers } from '../utils/userFilters';
import '../css/Pages.css';
import '../css/PackagesPage.css';
import '../css/Calendar.css';
import { toast } from 'react-toastify';
import * as FiIcons from 'react-icons/fi';
import * as MdIcons from 'react-icons/md';

const FiCalendar = FiIcons.FiCalendar as unknown as React.FC;
const MdCheckCircle = MdIcons.MdCheckCircle as unknown as React.FC;
const MdCancel = MdIcons.MdCancel as unknown as React.FC;
const MdAccessTime = MdIcons.MdAccessTime as unknown as React.FC;
const MdEventBusy = MdIcons.MdEventBusy as unknown as React.FC;

const PAGE_SIZE = 20;

const AttendancePage = () => {
  const hasFetched = useRef(false);
  const { user } = useContext(AuthContext);
  const permissions = usePermissions();
  const role = useRole();
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [teamUsers, setTeamUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  
  // Calculate default date range for last 30 days
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29); // Last 30 days (including today)
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  };

  const defaultRange = getDefaultDateRange();

  // Filter state
  const [startDate, setStartDate] = useState<string>(defaultRange.start);
  const [endDate, setEndDate] = useState<string>(defaultRange.end);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const loadAttendances = async () => {
    try {
      setLoading(true);
      const params: any = {};
      
      // Role-based filtering
      if (permissions.canViewAllAttendance) {
        // Admin: can view all, use selectedUserId if provided
        if (selectedUserId) {
          params.userId = selectedUserId;
        }
      } else {
        // Non-admin: only own attendance (or team for managers)
        const currentUserId = user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : 0;
        if (role === 'sales-manager' || role === 'ops') {
          // Managers can view team attendance
          if (selectedUserId) {
            // Check if selected user is in team
            const isTeamMember = teamUsers.some(u => u.id === selectedUserId);
            params.userId = isTeamMember ? selectedUserId : currentUserId;
          } else {
            // Show own + team attendance (backend should handle this)
            params.includeTeam = true; // Backend should support this
          }
        } else {
          // Sales: only own attendance
          params.userId = currentUserId;
        }
      }

      // Apply date range filter
      if (startDate && endDate) {
        if (startDate === endDate) {
          // Single date
          params.date = startDate;
        } else {
          // Date range
          params.startDate = startDate;
          params.endDate = endDate;
        }
      }

      const data = await fetchAttendances(params);
      setAttendances(Array.isArray(data) ? data : []);
    } catch (error: any) {
      toast.error('Failed to load attendances');
      setAttendances([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const userList = await fetchUsers();
      const allUsers = filterEnabledRegularUsers(Array.isArray(userList) ? userList : []);
      setUsers(allUsers);
      
      // Load team users for managers
      if (role === 'sales-manager' || role === 'ops') {
        const currentUserId = user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : 0;
        // Filter users where managerId matches current user
        const team = allUsers.filter(u => u.managerId === currentUserId);
        setTeamUsers(team);
      }
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadUsers();
    loadAttendances(); // Load with default filter (last 30 days)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedUserId]);

  useEffect(() => {
    if (hasFetched.current) {
      loadAttendances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedUserId]);

  const enabledUserIds = useMemo(() => {
    return new Set(users.map(u => u.id).filter((id): id is number => typeof id === 'number'));
  }, [users]);

  const visibleAttendances = useMemo(() => {
    if (users.length === 0) return [];
    return attendances.filter(a => enabledUserIds.has(a.userId));
  }, [attendances, enabledUserIds, users.length]);

  const totalPages = Math.max(1, Math.ceil(visibleAttendances.length / PAGE_SIZE));
  const pagedAttendances = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return visibleAttendances.slice(start, start + PAGE_SIZE);
  }, [visibleAttendances, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);


  const getUserName = (userId: number) => {
    const user = users.find(u => u.id === userId);
    return user?.name || `User ${userId}`;
  };

  const getUserEmail = (userId: number) => {
    const user = users.find(u => u.id === userId);
    return user?.email || '';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'present':
        return <span className="crm-status-icon present"><MdCheckCircle /></span>;
      case 'absent':
        return <span className="crm-status-icon absent"><MdCancel /></span>;
      case 'half-day':
        return <span className="crm-status-icon half-day"><MdAccessTime /></span>;
      case 'leave':
        return <span className="crm-status-icon leave"><MdEventBusy /></span>;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ');
  };

  return (
    <Layout>
      <div className="crm-page packages-page">
        <div className="packages-header">
          <div>
            <h2>Attendance</h2>
            <p className="page-hint">Review attendance history across the selected date range and team scope.</p>
          </div>
          <div className="packages-actions">
          </div>
        </div>

        <div className="packages-filters">
          <div className="crm-filter-group">
            <FiCalendar />
            <label className="crm-filter-label">Date Range</label>
            <button
              className="btn-secondary"
              onClick={() => setShowDatePicker(true)}
              type="button"
            >
              <span>
                {startDate === endDate
                  ? new Date(startDate).toLocaleDateString()
                  : `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
                }
              </span>
              <FiCalendar />
            </button>
          </div>

          {permissions.canViewAllAttendance && (
            <div className="crm-filter-group">
              <label className="crm-filter-label">User</label>
              <select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : undefined)}
                className="filter-select"
              >
                <option value="">All Users</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {(role === 'sales-manager' || role === 'ops') && !permissions.canViewAllAttendance && (
            <div className="crm-filter-group">
              <label className="crm-filter-label">User</label>
              <select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : undefined)}
                className="filter-select"
              >
                <option value="">All Team Members</option>
                <option value={user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : 0}>
                  {user?.name || 'Me'}
                </option>
                {teamUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {showDatePicker && (
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onDateChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
            onClose={() => setShowDatePicker(false)}
          />
        )}

        <div className="packages-table-wrapper">
          <table className="packages-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Status</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Location</th>
                <th>Check-in Selfie</th>
                <th>Check-out Selfie</th>
              </tr>
            </thead>
            <tbody>
              {pagedAttendances.map((attendance, idx) => (
                <tr key={attendance.id ?? idx}>
                  <td>{new Date(attendance.date).toLocaleDateString()}</td>
                  <td>
                    <div className="crm-user-cell">
                      <div className="crm-user-name">{getUserName(attendance.userId)}</div>
                      <div className="crm-user-email">{getUserEmail(attendance.userId)}</div>
                    </div>
                  </td>
                  <td>
                    <div className="crm-status">
                      {getStatusIcon(attendance.status)}
                      <span>{getStatusLabel(attendance.status)}</span>
                    </div>
                  </td>
                  <td>{attendance.checkInTime || '-'}</td>
                  <td>{attendance.checkOutTime || '-'}</td>
                  <td>
                    {attendance.latitude && attendance.longitude ? (
                      <div className="crm-meta">
                        <div>{attendance.latitude.toFixed(4)}</div>
                        <div>{attendance.longitude.toFixed(4)}</div>
                        <a
                          className="crm-link"
                          href={`https://www.google.com/maps?q=${attendance.latitude},${attendance.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Map
                        </a>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {attendance.selfieUrl ? (
                      <img
                        src={attendance.selfieUrl}
                        alt="Check-in selfie"
                        className="crm-thumbnail"
                        onClick={() => {
                          const newWindow = window.open();
                          if (newWindow) {
                            newWindow.document.write(`<img src="${attendance.selfieUrl}" style="max-width:100%;height:auto;" />`);
                          }
                        }}
                        title="Click to view full size"
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {(attendance.checkoutSelfieUrl || (attendance as any).checkoutSelfie) ? (
                      <img
                        src={(attendance.checkoutSelfieUrl || (attendance as any).checkoutSelfie) as string}
                        alt="Check-out selfie"
                        className="crm-thumbnail"
                        onClick={() => {
                          const src = (attendance.checkoutSelfieUrl || (attendance as any).checkoutSelfie) as string;
                          const newWindow = window.open();
                          if (newWindow) {
                            newWindow.document.write(`<img src="${src}" style="max-width:100%;height:auto;" />`);
                          }
                        }}
                        title="Click to view full size"
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
              {visibleAttendances.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty-state">
                    {loading ? 'Loading...' : 'No attendance records found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

    </Layout>
  );
};

export default AttendancePage;
